import React, { useMemo, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { Table, Pagination, Spin, Empty } from 'antd';
import type { SelectProps } from 'antd';
import { testPaginationItemRender, toTestAttrValue, withTestColumnAttrs, type TestAttrs } from '@utils/testAttr';
import PageLayout from '@components/layout/PageLayout';
import { useAuditData } from '@hooks/graphql/useAuditData';
import { useAuditClientFilter } from '@hooks/useAuditClientFilter';
import AuditFilterPanel, { AuditFilters, getDateRange } from './auditFilterPanel';
import { buildAuditColumns, ExpandedRow } from './auditTableConfig';
import styles from './Audit.module.css';

const initialDateRange = getDateRange('thisMonth', null);

const Audit = () => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<AuditFilters>({
    ...initialDateRange,
    meterId: null,
    vendorName: null,
  });

  const { data, totalCount, loading, currentPage, pageSize, onChangePage } = useAuditData(
    undefined,
    undefined,
    filters.startDate || undefined,
    filters.endDate || undefined,
    filters.meterId ?? null,
    filters.suppressQuery,
    filters.vendorName ?? null,
  );

  const filteredData = useAuditClientFilter(data, filters.actorFilter ?? '', filters.windFarmFilter ?? '');

  const toggleExpand = (id: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const columns = buildAuditColumns(expandedKeys, toggleExpand);

  const emptyDescription = useMemo(() => {
    if (filters.meterId) {
      return (
        <span>
          No audit activity found for this meter in the selected time frame.
          <br />
          Try a wider range — e.g. switch from Last 7 Days to This Month or a Custom Range.
        </span>
      );
    }
    if (filters.vendorName) {
      return (
        <span>
          No audit activity found for this vendor in the selected time frame.
          <br />
          Try a wider range — e.g. switch from Last 7 Days to This Month or a Custom Range.
        </span>
      );
    }
    return 'No audit events';
  }, [filters.meterId, filters.vendorName]);

  return (
    <PageLayout title="Audit Trail">
      <AuditFilterPanel recordCount={filteredData.length} onChange={setFilters} />
      <div className={styles.tableWrapper}>
        <Spin spinning={loading} data-testid="audit-table-spinner" data-loading={String(loading)}>
          <Table
            columns={withTestColumnAttrs(columns, 'audit-value', { expand: 'Expand' })}
            dataSource={filteredData}
            rowKey="id"
            pagination={false}
            size="middle"
            data-testid="audit-table"
            data-row-count={String(filteredData.length)}
            onRow={(record) =>
              ({
                'data-testid': 'audit-row',
                'data-audit-id': String(record.id),
                'data-action': toTestAttrValue(record.action),
                'data-table-name': record.tableName ?? '',
                'data-expandable': String(record.before !== null || record.after !== null),
                'data-expanded': String(expandedKeys.has(record.id)),
              }) as TestAttrs
            }
            locale={{ emptyText: <Empty description={emptyDescription} data-testid="audit-empty-state" /> }}
            expandable={{
              expandedRowKeys: Array.from(expandedKeys),
              expandedRowRender: (record) => <ExpandedRow record={record} />,
              rowExpandable: (record) => record.before !== null || record.after !== null,
              showExpandColumn: false,
            }}
          />
        </Spin>

        {totalCount > 0 && (
          <div className={styles.paginationBar}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalCount}
              onChange={onChangePage}
              showSizeChanger={{ 'data-testid': 'page-size-select' } as SelectProps}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showTotal={(total) => <span data-testid="pagination-total">{`${total} records`}</span>}
              itemRender={testPaginationItemRender}
              data-testid="table-pagination"
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default Audit;
